import React from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';

export default function LanguageToggle() {
  const { language, toggleLanguage, isTranslating } = useLanguage();

  return (
    <button
      onClick={toggleLanguage}
      disabled={isTranslating}
      className={`relative flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-b from-[#2a2a3e] to-[#1a1a2e] border border-white/10 hover:border-white/20 transition-all duration-300 shadow-lg min-w-[110px] ${
        isTranslating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      }`}
      aria-label={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
    >
      {/* Loading Overlay */}
      {isTranslating && (
        <div className="absolute inset-0 bg-[#1a1a2e]/80 rounded-full flex items-center justify-center z-10">
          <svg
            className="animate-spin h-5 w-5 text-purple"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}
      
      {/* Sliding Flag Container */}
      <div className="relative w-full h-7 flex items-center">
        {/* Sliding Background Track */}
        <div className={`absolute inset-0 rounded-full bg-white/5 flex items-center transition-all duration-300 ${
          language === 'en' ? 'justify-end pr-3' : 'justify-start pl-3'
        }`}>
          {/* Language Code - Positioned at opposite end of flag */}
          <span className="text-sm font-bold uppercase tracking-widest text-white/80">
            {language}
          </span>
        </div>

        {/* Animated Flag Slider */}
        <div
          className={`absolute top-0 w-7 h-7 rounded-full bg-gradient-to-br from-white/20 to-white/5 shadow-md border border-white/20 flex items-center justify-center transition-all duration-500 ease-out ${
            language === 'en' 
              ? 'left-0 translate-x-0' 
              : 'left-full -translate-x-full'
          }`}
        >
          {/* Flag Container with Rotation Animation */}
          <div className="relative w-5 h-5">
            {/* US Flag */}
            <div
              className={`absolute inset-0 transition-all duration-500 ${
                language === 'en'
                  ? 'opacity-100 scale-100 rotate-0'
                  : 'opacity-0 scale-50 rotate-180'
              }`}
            >
              <svg
                className="w-5 h-5 rounded-full"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" fill="#B22234" rx="12" />
                <rect y="1.8" width="24" height="1.8" fill="white" />
                <rect y="5.4" width="24" height="1.8" fill="white" />
                <rect y="9" width="24" height="1.8" fill="white" />
                <rect y="12.6" width="24" height="1.8" fill="white" />
                <rect y="16.2" width="24" height="1.8" fill="white" />
                <rect y="19.8" width="24" height="1.8" fill="white" />
                <rect width="10" height="12.8" fill="#3C3B6E" rx="0" />
                <circle cx="2.5" cy="2.5" r="0.4" fill="white" />
                <circle cx="4.5" cy="2.5" r="0.4" fill="white" />
                <circle cx="6.5" cy="2.5" r="0.4" fill="white" />
                <circle cx="2.5" cy="4.5" r="0.4" fill="white" />
                <circle cx="4.5" cy="4.5" r="0.4" fill="white" />
                <circle cx="6.5" cy="4.5" r="0.4" fill="white" />
                <circle cx="2.5" cy="6.5" r="0.4" fill="white" />
                <circle cx="4.5" cy="6.5" r="0.4" fill="white" />
                <circle cx="6.5" cy="6.5" r="0.4" fill="white" />
                <circle cx="2.5" cy="8.5" r="0.4" fill="white" />
                <circle cx="4.5" cy="8.5" r="0.4" fill="white" />
                <circle cx="6.5" cy="8.5" r="0.4" fill="white" />
              </svg>
            </div>

            {/* Spanish Flag */}
            <div
              className={`absolute inset-0 transition-all duration-500 ${
                language === 'es'
                  ? 'opacity-100 scale-100 rotate-0'
                  : 'opacity-0 scale-50 -rotate-180'
              }`}
            >
              <svg
                className="w-5 h-5 rounded-full"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" fill="#AA151B" rx="12" />
                <rect y="6" width="24" height="12" fill="#F1BF00" />
                <rect y="18" width="24" height="6" fill="#AA151B" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
